import React, { useState, useEffect } from 'react';
import { X, UserPlus, Save, Loader } from 'lucide-react';
import { db } from '../../db';
import { config } from '../../config';

const UserProvisioningModal = ({ onClose }) => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Dropdown Data
    const [orgs, setOrgs] = useState([]);
    const [roles, setRoles] = useState([]);
    const [programs, setPrograms] = useState([]);

    // Form Data
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        orgId: '',
        orgRole: '',
        role: 'CLIENT', // Default
        billing: 'Trial',
        program: '',
        existUrl: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            // 1. Fetch Orgs (Organizations tab)
            const orgData = await db.tabData.where('tabName').equals('Organizations').first();
            if (orgData?.data) {
                setOrgs(orgData.data.map(o => ({ id: o.OrgID, name: o.Org_Name })));
            }

            // 2. Fetch Validation Options (Master_Validation tab)
            const valData = await db.tabData.where('tabName').equals('Master_Validation').first();
            if (valData?.data) {
                // Column 'Org_Role_Options' is usually mapped from header 'Org_Role_Options'
                // We need to check the actual data structure. Assuming flat list or column based.
                // Fallback hardcoded if data structure is complex or missing
                const roleOpts = valData.data.map(r => r.Org_Role_Options).filter(Boolean);
                setRoles(roleOpts.length > 0 ? roleOpts : ['Owner', 'Manager', 'Staff', 'Admin']);
            }

            // 3. Fetch Programs (Master_Products tab)
            const prodData = await db.tabData.where('tabName').equals('Master_Products').first();
            if (prodData?.data) {
                setPrograms(prodData.data.map(p => p.Product_Name).filter(Boolean));
            }
        } catch (e) {
            console.error("Error loading provisioning data", e);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            // Generate User ID (Mock logic similar to GAS)
            const date = new Date();
            const idSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
            const userId = `${formData.role === 'Customer' ? 'CUST' : 'USR'}-${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}-${idSuffix}`;

            const payload = {
                UserID: userId,
                Name: formData.name,
                Email: formData.email,
                Phone: formData.phone,
                OrgID: formData.orgId,
                OrgRole: formData.orgRole,
                Role: formData.role.toUpperCase(), // Ensure uppercase match with backend
                BillingStatus: formData.billing,
                Program: formData.program,
                ExistingURL: formData.existUrl,
                Date: date.toISOString()
            };

            // Post to n8n Webhook
            // NOTE: In production, ensure this URL matches the 'ADD_USER' webhook in Code.gs
            const WEBHOOK_URL = `${config.API_BASE_URL}/webhook/hq-add-user`;

            await fetch(WEBHOOK_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            alert(`User ${formData.name} provisioned successfully! ID: ${userId}`);
            onClose();

        } catch (error) {
            console.error("Provisioning Error", error);
            alert("Failed to provision user: " + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white p-8 rounded-2xl flex flex-col items-center">
                <Loader className="animate-spin text-blue-600 mb-4" size={32} />
                <p className="font-bold text-slate-600">Loading Configuration...</p>
            </div>
        </div>
    );}

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-200 relative flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="bg-slate-50 border-b border-slate-100 p-6 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-xl text-blue-600">
                            <UserPlus size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-slate-800">Provision User</h2>
                            <p className="text-xs text-slate-500 font-medium">Add new client or team member</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Body (Scrollable) */}
                <div className="p-6 overflow-y-auto custom-scrollbar">
                    <form id="provForm" onSubmit={handleSubmit} className="space-y-6">

                        {/* Section 1: Identity */}
                        <div className="bg-white border boundary-blue-100 p-4 rounded-2xl space-y-4">
                            <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider border-b border-slate-100 pb-2 mb-2">
                                👤 Client Details
                            </h3>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Full Name *</label>
                                <input required id="name" value={formData.name} onChange={handleChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all" placeholder="e.g. John Doe" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Email *</label>
                                    <input required type="email" id="email" value={formData.email} onChange={handleChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-500 outline-none" placeholder="john@example.com" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">WhatsApp</label>
                                    <input type="tel" id="phone" value={formData.phone} onChange={handleChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-500 outline-none" placeholder="+27..." />
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Organization */}
                        <div className="bg-white border boundary-blue-100 p-4 rounded-2xl space-y-4">
                            <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider border-b border-slate-100 pb-2 mb-2">
                                🏢 Organization
                            </h3>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Organization</label>
                                <select id="orgId" value={formData.orgId} onChange={handleChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-500 outline-none">
                                    <option value="">-- Solo (Self-Paced) --</option>
                                    {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Org Role</label>
                                <select id="orgRole" value={formData.orgRole} onChange={handleChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-500 outline-none">
                                    <option value="">-- Select Role --</option>
                                    {roles.map((r, i) => <option key={i} value={r}>{r}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Section 3: Settings */}
                        <div className="bg-white border boundary-blue-100 p-4 rounded-2xl space-y-4">
                            <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider border-b border-slate-100 pb-2 mb-2">
                                ⚙️ Access & Billing
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">User Role</label>
                                    <select id="role" value={formData.role} onChange={handleChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-500 outline-none">
                                        <option value="CLIENT">Coaching Client</option>
                                        <option value="CUSTOMER">Service Customer</option>
                                        <option value="COACH">Coach / Partner</option>
                                        <option value="ADMIN">Admin</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Billing</label>
                                    <select id="billing" value={formData.billing} onChange={handleChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-500 outline-none">
                                        <option value="Trial">Start 14-Day Trial</option>
                                        <option value="Active">Active / Paid</option>
                                        <option value="Suspended">Suspended</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Program / Package</label>
                                <select id="program" value={formData.program} onChange={handleChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-500 outline-none">
                                    <option value="">-- Select Program --</option>
                                    {programs.map((p, i) => <option key={i} value={p}>{p}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Existing Sheet URL (Import)</label>
                                <input id="existUrl" value={formData.existUrl} onChange={handleChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-500 outline-none text-xs font-mono" placeholder="https://docs.google.com/spreadsheets/d/..." />
                            </div>
                        </div>

                    </form>
                </div>

                {/* Footer */}
                <div className="bg-slate-50 border-t border-slate-100 p-6">
                    <button
                        form="provForm"
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Processing...' : <><Save size={20} /> Provision Account</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserProvisioningModal;
